import React, { useState, useRef, useEffect } from 'react';
import {
  Checkbox,
  ListItemIcon,
  ListItemText,
  TextField,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  Button,
  Divider,
  MenuItem,
  Paper,
  ClickAwayListener,
  Chip
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ClearIcon from '@material-ui/icons/Clear';
import './BTTab/BTTab.css';

interface Option {
  label: string;
  value: string;
}

interface MultiSelectDropdownProps {
  label: string;
  options: Option[];
  selected: string[];
  onApply: (selected: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selected,
  onApply,
  placeholder,
  disabled = false
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  const getDisplayValue = () => {
    return open && search ? search : '';
  };

  const handleToggle = (value: string) => {
    const newSelected = selected.includes(value)
      ? selected.filter(v => v !== value)
      : [...selected, value];
    onApply(newSelected);
    setSearch('');
  };

  const handleClearAll = () => {
    onApply([]);
    setSearch('');
    setOpen(false);
  };

  const filteredOptions = options.filter(opt =>
    opt.label?.toLowerCase().includes(search.toLowerCase())
  );

  const handleClickAway = () => {
    setOpen(false);
    setSearch('');
  };

  return (
    <>
      <style>{`
        .MuiListItemIcon-root { min-width: 0 !important; }
        .custom-multiselect-textfield .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
          border-color: #7B61FF !important;
          border-width: 2px !important;
        }
      `}</style>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
          <TextField
            inputRef={inputRef}
            variant="outlined"
            placeholder={selected.length === 0 ? placeholder || label : ''}
            onChange={e => {
              setSearch(e.target.value);
              if (!open) setOpen(true);
            }}
            fullWidth
            disabled={disabled}
            onFocus={() => {
              if (!disabled) {
                setSearch('');
                setOpen(true);
              }
            }}
            value={getDisplayValue()}
            InputProps={{
              startAdornment:
                selected.length > 0 ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: 4, marginLeft: 4 }}
                  >
                    {selected.slice(0, 1).map(val => {
                      const opt = options.find(o => o.value === val);
                      return (
                        <Chip
                          key={val}
                          label={opt ? opt.label : val}
                          size="small"
                          style={{
                            marginRight: 2,
                            background: '#F3F2F7',
                            fontWeight: 500
                          }}
                          onDelete={e => {
                            e.stopPropagation();
                            const newSelected = selected.filter(s => s !== val);
                            onApply(newSelected);
                          }}
                          deleteIcon={<ClearIcon style={{ fontSize: 18 }} />}
                        />
                      );
                    })}
                    {selected.length > 1 && (
                      <Chip
                        label={`+${selected.length - 1}`}
                        size="small"
                        style={{ background: '#F3F2F7', fontWeight: 500 }}
                      />
                    )}
                  </Box>
                ) : (
                  undefined
                ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    disabled={disabled}
                    onClick={() => !disabled && setOpen(prev => !prev)}
                  >
                    <ArrowDropDownIcon />
                  </IconButton>
                </InputAdornment>
              ),
              style: {
                borderRadius: 4,
                background: '#fff',
                fontSize: 15
              }
            }}
            className="custom-multiselect-textfield"
          />

          {open && (
            <Paper
              elevation={4}
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 320,
                minHeight: 120,
                width: '100%',
                border: '1px solid #e9e4e4'
              }}
            >
              <Box
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  maxHeight: 220,
                  padding: '0 0 0 0'
                }}
              >
                {filteredOptions.length === 0 ? (
                  <Typography
                    color="textSecondary"
                    style={{ padding: 16, textAlign: 'center' }}
                  >
                    No options
                  </Typography>
                ) : (
                  filteredOptions.map(opt => (
                    <MenuItem
                      key={opt.value}
                      onClick={() => handleToggle(opt.value)}
                      selected={selected.includes(opt.value)}
                      style={{ borderRadius: 6, marginBottom: 2 }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selected.includes(opt.value)}
                          tabIndex={-1}
                          disableRipple
                          style={{ fill: '#6F42F5', color: '#6F42F5' }}
                        />
                      </ListItemIcon>
                      <ListItemText primary={opt.label} />
                    </MenuItem>
                  ))
                )}
              </Box>
              <Divider style={{ margin: 0 }} />
              <Box
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                px={2}
                py={1}
                style={{
                  background: '#fff',
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 2
                }}
              >
                <Button
                  onClick={handleClearAll}
                  style={{
                    textTransform: 'none',
                    color: '#7B61FF'
                  }}
                >
                  Clear All
                </Button>
              </Box>
            </Paper>
          )}
        </div>
      </ClickAwayListener>
    </>
  );
};

export default MultiSelectDropdown;
